/** @jsx jsx */

import {
  Fragment as _,
  useLayoutEffect,
  createContext,
  useEffect,
} from 'react';
import { jsx } from '@emotion/core';
import { useSelector, useDispatch } from 'react-redux';
import Header from 'components/Header';
import { focusBlockAction } from 'actions/focusBlockAction';
import {
  getBlock,
  getTopicGroupFromState,
  getTopicsFromGroup,
  getInteractionsFromTopicAndState,
} from 'selectors/block';
import { ThemeProvider } from 'emotion-theming';
import { theme } from 'theme';
import Bookmark from './Bookmark';
import { ErrorModal } from 'components/errorModal';
import { FontManager } from './Fonts';
import TopicContainer from './TopicContainer';
import AssessmentContainer from './AssessmentContainer';
import { SCORMRenderer } from '../lib/Scorm/SCORM';
import { Block } from '@chameleoncreator/creator-app';
import { createScorm } from 'factories/scorm';
import getCurrentLocation from 'services/getCurrentLocation';
import getTopics from 'services/apiHelpers/getTopics';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import { updateInteractionsOpenAction } from 'actions/updateInteractionsOpenAction';
import windowScrollInfo from './windowScrollInfo';
import Interactions_Editor from './Interactions_Editor';
import Interactions_Module from './Interactions_Module';
import pubSub, { usePubSub } from 'services/pubSub';
import getBlockById from 'services/apiHelpers/getBlockById';
import { setExitAction } from 'actions/setExitAction';

window.location.hash = '';

export const ModuleContext = createContext({
  passMark: 80,
  exit: () => {},
});

const Module = props => {
  //
  const state = useSelector((state: any) => state);

  // create re_selectors for these ?
  const { currentTopic: topicId, currentInteraction: interactionId } = state;

  const { blocks, rootBlockId } = state;

  const group = getTopicGroupFromState(state);
  const topicList = getTopicsFromGroup(state, group);

  const dispatch = useDispatch();

  const currentTopicIndex = topicId
    ? topicList.findIndex(t => t.id === topicId)
    : 0;

  // const setCurrentBlock = data => {
  //   dispatch(focusBlockAction(data));
  // };

  // const updateInteractionsSeen = id => {
  //   dispatch({
  //     type: 'UPDATE_INTERACTIONS_SEEN',
  //     data: {
  //       id,
  //     },
  //   });
  // };

  // const updateInteractionsOpen = id => {
  //   dispatch(updateInteractionsOpenAction(id));
  // };

  useLayoutEffect(() => {
    windowScrollInfo(null, topicId);
  }, [topicId]);

  useEffect(() => {
    // put this somewhere else
    const compareIds = (topicInteraction, id) => {
      return [topicInteraction.props.sId, topicInteraction.id]
        .map(sId => String(sId))
        .includes(id);
    };

    const navToHash = e => {
      console.log('navToHash.start');

      e.preventDefault();

      // return;

      let hash: any = String(window.location.hash)
        .replace(/#/, '')
        .split('/');

      console.log('navToHash.hash', hash);

      if (hash.length !== 2) {
        console.log('navToHash.returns here');
        (window.locationHistory as any) = null;
        return;
      }

      // for legacy we'll use the id. format will be the same as
      const [topicSid, interactionSId] = hash;

      const targetTopic = getTopics().find((t: any) => compareIds(t, topicSid));

      // this'll only be for when we're testing
      if (!targetTopic) {
        return;
      }

      const interactionsInTopic = getInteractionsInTopic(targetTopic);

      const targetInteraction = interactionsInTopic.find((i: any) =>
        compareIds(i, interactionSId)
      );

      // be safe. bail if not
      if (!targetInteraction) {
        return;
      }

      // if we're here. we're gonna nav
      // set history
      (window.locationHistory as any) = getCurrentLocation();
      //

      const targetInteractionIndex = interactionsInTopic.findIndex((i: any) =>
        compareIds(i, interactionSId)
      );

      const updatedInteractionsSeen: string[] = [];
      const updatedInteractionsOpen: string[] = [];

      interactionsInTopic.forEach((i: any, index: number) => {
        if (index <= targetInteractionIndex) {
          updatedInteractionsSeen.push(i.id);
          updatedInteractionsOpen.push(i.id);
        }
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_SEEN',
        data: updatedInteractionsSeen,
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_OPEN',
        data: updatedInteractionsOpen,
      });

      targetTopic?.id && setCurrentTopic(targetTopic.id);
      window.location.href = `#i${targetInteraction.id}`;
      //

      console.log('navToHash.ends');
    };

    window.addEventListener('hashchange', navToHash);

    // maybe we can toggle all this on / off when scrolling

    return () => {
      window.removeEventListener('hashchange', navToHash);
    };
  }, []);

  useLayoutEffect(() => {
    if (!rootBlockId) {
      return;
    }
    window.scrollTo(0, 0);
  }, [currentTopicIndex]);

  if (!rootBlockId) {
    return null;
  }

  const topic: any = topicList[currentTopicIndex > -1 ? currentTopicIndex : 0];

  // update has with topic name
  // do we want to keep the spaces ' ' > %20
  // assume screen readers etc know how to manage them ( and not read that as literal )
  // const parsedTopicName = topic.props.name.replace(/#/g, '');
  // window.location.hash = parsedTopicName;

  let interactions = topic
    ? getInteractionsFromTopicAndState(state, getBlock(blocks, topic.id))
    : [];

  //

  // console.log(interactions);

  // interactions = getBlockById(topic.id)
  //   .children.map(b => {
  //     return getBlockById(b.id);
  //   })[0]
  //   .children.map(b => {
  //     return getBlockById(b.id);
  //   });

  // console.log(' >>> ', interactions);

  let scormBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'SCORM') as any
  ) as Block;

  if (!scormBlock) {
    scormBlock = createScorm();
  }

  const runExit = () => {
    if (scormBlock.props!.scormControlledExit) {
      dispatch(setExitAction());
    } else {
      dispatch(setExitAction());
      setTimeout(() => {
        window.top.close();
      }, 200);
    }
  };

  const moduleBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'Module') as any
  ) as Block;
  let passMark = moduleBlock.props!.passMark || 80;

  const TopicAssessmentContainer = !topic.props.asAssessment
    ? TopicContainer
    : AssessmentContainer;

  return (
    <ModuleContext.Provider value={{ passMark, exit: runExit }}>
      <FontManager />
      <ErrorModal>
        {error => (
          <div>
            <h1>Oh no :(</h1>
            <p>Something has gone wrong. The error message provided is:</p>
            <p>
              <strong>"{error.message}".</strong>
            </p>
            <p>
              We have been alerted to this error, however if it is consistently
              occuring please fell free to notify us by clicking on the support
              button in the bottom right hand corner.
            </p>
          </div>
        )}
      </ErrorModal>
      <SCORMRenderer key="scorm" {...scormBlock} />
      <ThemeProvider theme={theme}>
        <Header currentTopicIndex={currentTopicIndex} topics={topicList} />
        <TopicAssessmentContainer
          key={`topic${currentTopicIndex}`}
          state={state}
          topic={topic}
        >
          {window.dev() ? (
            <Interactions_Editor
              currentTopicId={topic.id}
              currentInteractionId={interactionId}
              interactions={interactions}
            />
          ) : (
            <Interactions_Module
              key={interactions[0]?.id}
              interactions={interactions}
            />
          )}
        </TopicAssessmentContainer>
        {/* <TopicContainer /> */}
        {/* <div id="modal-container" role="dialog" /> */}
      </ThemeProvider>
      <Bookmark />
    </ModuleContext.Provider>
  );
};

export default Module;
